import React, {useEffect, useMemo, useState} from 'react';

import {Assignee} from '../types/vanLoadAt';
import BarcodeList from '../components/PrintScanBarcodes/barcodeList';
import {Box} from '../../../src/components/core/view';
import CustomerOrders from '../components/PrintScanBarcodes/customerOrders';
import Header from '../../../src/components/composites/Header/Header';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {OrderInfo} from '@buncha/config/graphQL/type';
import {PrintScanBarcodesContext} from '../context/printScanBarcodes';
import PrintingStickersBanner from '../components/PrintScanBarcodes/printingStickersBanner';
import ScreenHeader from '../../../src/common/components/screenHeader';
import {StyleSheet} from 'react-native';
import WaitingForShoppersBanner from '../components/common/waitingForShoppersBanner';
import {isWeb} from '../../../src/utils/common';
import {uniqBy} from 'lodash';
import {useColorThemeType} from '../../../src/appStyles/hooks/theme';
import {useGetGroupedOrders} from '../hooks/VanLoadAt';
import {useGetLocale} from '@buncha/localization/hooks';
import {usePrintScanBarcodesReducer} from '../hooks/printScanBarcodes';

type Props = NativeStackScreenProps<any, any>;

interface RouteParams {
  runId?: number;
  buyerId?: number;
}

const PrintScanBarcodes: React.FC<Props> = props => {
  const [hasBagSubmittedByAll, setHasBagSubmittedByAll] = useState(true);

  const [state, dispatch] = usePrintScanBarcodesReducer();
  const {title} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const theme = useColorThemeType('main');
  const isWebMode = isWeb();
  const routeParams: RouteParams | undefined = props?.route?.params;
  const runId = Number(routeParams?.runId ?? 0);
  const buyerId = Number(routeParams?.buyerId ?? 0);
  const {getGroupedOrder, loading, groupedOrderData} =
    useGetGroupedOrders(dispatch);
  const buyerOrders = useMemo(
    () => groupedOrderData[0]?.orders ?? [],
    [groupedOrderData],
  );
  const shopperList: Assignee[] = uniqBy(
    buyerOrders.map(order => {
      return order?.orderAssignee?.assignee;
    }),
    'phoneNumber',
  );

  const orderInfoVariables = useMemo(() => {
    const orderIds: number[] = [];
    const b2BOrderIds: number[] = [];
    const orderInfo: OrderInfo[] = [];

    buyerOrders.forEach(order => {
      const id = order.id;
      const run = order.run;
      const store = run.store;
      const storeMaster = store.storeMaster;
      const organizationId = storeMaster.organizationId;
      let info: any = {
        orderId: id,
      };

      if (organizationId) {
        b2BOrderIds.push(id);
        info = {
          ...info,
          organizationId: organizationId,
        };
      } else {
        orderIds.push(id);
      }

      orderInfo.push(info);
    });
    return {
      orderIds: orderIds,
      b2BOrderIds: b2BOrderIds,
      orderInfo: orderInfo,
    };
  }, [buyerOrders]);

  const returnContext = useMemo(() => {
    return {state, dispatch};
  }, [dispatch, state]);

  useEffect(() => {
    if (runId && buyerId) getGroupedOrder(runId, buyerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId, runId]);

  const getTotalCount = useMemo(() => {
    let count = 0;
    buyerOrders?.map(order => {
      if (order?.bagsUsed) count += order.bagsUsed;
      else setHasBagSubmittedByAll(false);
    });
    return count;
  }, [buyerOrders]);

  return (
    <PrintScanBarcodesContext.Provider value={returnContext}>
      <If
        condition={state.barcodesPrinting && groupedOrderData && getTotalCount}>
        <PrintingStickersBanner
          buyerName={groupedOrderData[0]?.buyer?.firstName || ''}
          bagCount={getTotalCount}
        />
      </If>
      <Header
        headerColor={theme?.background?.blue?.[300]}
        loading={loading}
        headerLeftComponent={isWebMode && <Box style={styles.webHeader} />}
        showDefaultLeftHeader>
        <ScreenHeader title={title?.printScanBarcodes} />
        <Box style={styles.parentBox}>
          <If condition={!hasBagSubmittedByAll}>
            <WaitingForShoppersBanner shopperList={shopperList} />
          </If>
          <CustomerOrders groupedOrderData={groupedOrderData} />
          <BarcodeList
            orderIds={orderInfoVariables?.orderIds}
            hasBagSubmittedByAll={hasBagSubmittedByAll}
            b2BOrderIds={orderInfoVariables?.b2BOrderIds}
            orderInfo={orderInfoVariables?.orderInfo}
          />
        </Box>
      </Header>
    </PrintScanBarcodesContext.Provider>
  );
};

export default React.memo(PrintScanBarcodes);
const styles = StyleSheet.create({
  parentBox: {
    paddingHorizontal: 20,
    flex: 1,
  },
  webHeader: {
    padding: 30,
  },
});
